/* eslint-disable react/prop-types */
import React from 'react';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';

import Config from '@/config/index';
import EventsAPI from 'api/events';

import {Page} from 'utils/page-utils';
import {fetchCleanedData} from 'router/router-utils';
import {loadable, isSearchPage} from 'router/routes';
import {GoogleMapsProvider} from 'common/google-map-context';

/*
    The Catch-all route corresponds to different page components based on a few factors:
    - whether this is a transient, monthly, event, or airport search
    - whether this is desktop or mobile experience

    The urls "caught" by this route are primarily the original search URLs
    which take the form spothero.com/[city]/[destination-slug]-parking and
    spothero.com/[city]/[destination-slug]-parking/[event-slug]. As our search pages
    grew in complexity and we added airport parking, the URL to component
    relationship became more difficult to manage.

    Other URLs caught by this route include:
    /parking-near-me (Search)
    /monthly-parking-near-me (Search)


    This route could be simplified by doing the following:
    1. Merging mobile and desktop features
    2. Completing the marketing work that allows us to completely drop support for original search URLs.

    https://spothero.atlassian.net/wiki/spaces/CONS/pages/1186529350/Marketing+Search+URL+Strategy
    https://spothero.atlassian.net/wiki/spaces/CONS/pages/1351582635/Search+URLS+-+What+to+Keep+What+to+Deprecate
*/

const SearchMobileTransient = loadable(() =>
    import('pages/search-transient-event-monthly/SearchMobileTransient')
);
const SearchMobileMonthly = loadable(() =>
    import('pages/search-transient-event-monthly/SearchMobileMonthly')
);
const SearchMobileEvent = loadable(() =>
    import('pages/search-transient-event-monthly/SearchMobileEvent')
);
const SearchMobileDestination = loadable(() =>
    import('pages/search-transient-event-monthly/SearchMobileDestination')
);
const SearchDesktopTransient = loadable(() =>
    import('pages/search-transient-event-monthly/SearchDesktopTransient')
);
const SearchDesktopMonthly = loadable(() =>
    import('pages/search-transient-event-monthly/SearchDesktopMonthly')
);
const SearchDesktopEvent = loadable(() =>
    import('pages/search-transient-event-monthly/SearchDesktopEvent')
);
const SearchDesktopDestination = loadable(() =>
    import('pages/search-transient-event-monthly/SearchDesktopDestination')
);
const NotFound = loadable(() => import('pages/not-found'));

const SearchPage = ({
    configData: {isMobile},
    isMonthly,
    destination,
    pageType,
    isPowerBooking,
}) => {
    if (isMonthly || isPowerBooking) {
        return isMobile ? <SearchMobileMonthly /> : <SearchDesktopMonthly />;
    }

    if (isMobile) {
        return pageType === 'event' ? (
            <SearchMobileEvent />
        ) : !isEmpty(destination) ? (
            <SearchMobileDestination />
        ) : (
            <SearchMobileTransient />
        );
    }

    return pageType === 'event' ? (
        <SearchDesktopEvent />
    ) : !isEmpty(destination) ? (
        <SearchDesktopDestination />
    ) : (
        <SearchDesktopTransient />
    );
};

function CatchAll({
    configData,
    isMonthly,
    destination,
    match,
    pageType,
    isPowerBooking,
}) {
    const {
        params: {page, section},
    } = match;

    if (
        (!section && includes(configData.citySlugs, page)) ||
        isSearchPage(page, section)
    ) {
        return (
            <GoogleMapsProvider>
                <SearchPage
                    configData={configData}
                    isMonthly={isMonthly}
                    destination={destination}
                    pageType={pageType}
                    isPowerBooking={isPowerBooking}
                />
            </GoogleMapsProvider>
        );
    }

    return <NotFound />;
}

/**
 *
 * @function mergeEventStateWithDefault
 * @param {string} page_type - The `pageType` string
 * @param {number} destinationId - The `destinationId` identifier
 * @param {object} state - The state to merge the events results with
 * @returns {object} The provided state object, merged with the returned event results
 */
async function mergeEventStateWithDefault({
    pageType,
    hasEvents,
    destinationId,
    state,
}) {
    if (pageType !== Page.EVENT && hasEvents && !Config.isMobile) {
        const events = await EventsAPI.get({destinationId}).then(
            res => res.data.data.results
        );

        return merge({}, state, {
            destination: {
                data: {
                    events,
                },
            },
        });
    }

    return state;
}

CatchAll.getInitialState = async ({state: initialState, route}) => {
    const state = await fetchCleanedData({
        state: initialState,
        route,
    });

    const {
        destination: {
            data: {id: destinationId, has_upcoming_events: hasEvents},
        },
        searchRequest: {pageType},
    } = state;

    const newState = await mergeEventStateWithDefault({
        pageType,
        hasEvents,
        destinationId,
        state,
    });

    if (route.location.pathname === '/monthly-parking-near-me') {
        newState.searchRequest.monthly = true;
    }

    return newState;
};

export default CatchAll;
